import React, { useEffect, useState } from "react";
import api from "../api/axios";

function MonthlyInvoice() {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showModal, setShowModal] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null); // Holds full row data
  const [feedback, setFeedback] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [feedbackHistory, setFeedbackHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generateInvoices, setGenerateInvoices] = useState([]);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [submittingGenerate, setSubmittingGenerate] = useState(false);

  const token = localStorage.getItem("accessToken");
  const branchId = JSON.parse(sessionStorage.getItem("selectedContext"))?.branch;

  // ============== FETCH BATCH LIST ==============
  const fetchInvoiceBatches = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/invoice-batch-list/${branchId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setBatches(res.data.data || []);
    } catch (err) {
      console.error("Error fetching invoice batches:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoiceBatches();
  }, []);

  // ============== DOWNLOAD PDF ==============
  const handleDownload = async (batchId) => {
    try {
      const response = await api.get(`/download-invoice-batch/${batchId}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `monthly-invoices-${batchId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Download failed:", error);
      alert("Unable to download invoice batch.");
    }
  };

  // ============== OPEN FEEDBACK MODAL ==============
  const openFeedbackModal = (batch) => {
    setSelectedBatch(batch);
    setFeedback("");
    setShowModal(true);
  };

  // ============== SUBMIT FEEDBACK ==============
  const submitFeedback = async () => {
    if (!feedback.trim()) {
      alert("Please enter feedback.");
      return;
    }

    setSubmitting(true);

    try {
      await api.post(
        "/send-invoice-feedback",
        {
          invoice_batch_id: selectedBatch.id,
          feedback: feedback,
          branch_id: branchId
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      alert("Feedback submitted successfully.");
      setShowModal(false);

    } catch (err) {
      console.error("Feedback submission error:", err);
      alert("Failed to submit feedback.");
    } finally {
      setSubmitting(false);
    }
  };

  const loadFeedbackHistory = async (invoiceBatchId) => {
    setHistoryLoading(true);
    setShowHistoryModal(true);

    try {
      const res = await api.get(`/invoices/${invoiceBatchId}/feedback-history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.data;

      setFeedbackHistory(data.history || []);
    } catch (err) {
      console.error("Error loading history:", err);
    } finally {
      setHistoryLoading(false);
    }
  };

  /* Load invoices and Generate journal entries */
  const openGenerateModal = async (batch) => {
    setSelectedBatch(batch);
    setShowGenerateModal(true);
    setSelectedInvoices([]);

    try {
      const res = await api.get(`/invoice-batch/${batch.id}/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log(res.data);
      // Only show first 25
      setGenerateInvoices(res.data.invoices); //setGenerateInvoices(res.data.invoices.slice(0, 25));
    } catch (error) {
      console.error(error);
      alert("Unable to load invoices.");
    }
  };

  const submitGenerate = async () => {

    if (selectedInvoices.length === 0) {
      alert("Select at least one invoice.");
      return;
    }

    setSubmittingGenerate(true);

    try {
      await api.post(
        "/generate-journal-entries",
        {
          invoice_batch_id: selectedBatch.id,
          branch_id: branchId,

          invoices: selectedInvoices.map(inv => ({
            id: inv.id,
            client_id: inv.client.ledger.id,
            invoice_no: inv.invoice_no,
            invoice_date: inv.invoice_date,
            amount: inv.total_amount,
            description: inv.description,

            // ▶ Include relation table data
            items: inv.items?.map(item => ({
              ledger_id: item.ledger_id,
              description: item.description,
              debit: 0,
              credit: item.amount
            })) || []
          }))
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      alert("Journal entries generated successfully.");
      setShowGenerateModal(false);
      fetchInvoiceBatches();

    } catch (error) {
      console.error(error);
      alert("Generation failed.");
    } finally {
      setSubmittingGenerate(false);
    }
  };

  const toggleInvoice = (invoice) => {
    setSelectedInvoices(prev => {
      const exists = prev.find(x => x.id === invoice.id);
      if (exists) {
        return prev.filter(x => x.id !== invoice.id);
      }
      return [...prev, invoice];
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      // select all invoices currently in table
      //const allIds = generateInvoices.map((inv) => inv.id);
      //setSelectedInvoices(allIds);
      setSelectedInvoices(generateInvoices);
    } else {
      // unselect all
      setSelectedInvoices([]);
    }
  };


  return (
    <>
      {/* Page Header */}
      <div className="row">
        <div className="col-12">
          <div className="page-title-box d-flex align-items-center justify-content-between">
            <h4 className="mb-sm-0">Monthly Invoice</h4>
            <div className="page-title-right">
              <ol className="breadcrumb m-0">
                <li className="breadcrumb-item"><a href="#">Accounts</a></li>
                <li className="breadcrumb-item active">Monthly Invoice</li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      {/* List Card */}
      <div className="row">
        <div className="col-xl-12">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h4 className="card-title mb-0">Invoice List</h4>
              <button
                className="btn btn-sm btn-outline-primary"
                onClick={fetchInvoiceBatches}
                disabled={loading}
              >
                {loading ? "Refreshing..." : "Refresh"}
              </button>
            </div>

            <div className="card-body">
              {loading ? (
                <p>Loading...</p>
              ) : batches.length === 0 ? (
                <p className="text-muted">No monthly invoice batches found.</p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-bordered table-striped align-middle">
                    <thead className="table-light">
                      <tr>
                        <th>Month</th>
                        <th>Branch</th>
                        <th>Invoice Date</th>
                        <th>Total Tenants</th>
                        <th className="text-center">Action</th>
                      </tr>
                    </thead>

                    <tbody>
                      {batches.map((batch, index) => (
                        <tr key={index}>
                          <td>
                            {new Date(batch.year, batch.month - 1).toLocaleDateString("en-US", {
                              month: "long",
                              year: "numeric",
                            })}
                          </td>

                          <td>{batch.branch?.name}</td>

                          <td>
                            {batch.created_at
                              ? new Date(batch.created_at).toLocaleDateString()
                              : "-"}
                          </td>

                          <td>{batch.invoices.length}</td>

                          <td className="text-center">

                            {/* FEEDBACK */}
                            {0 == batch.finalize && (<button
                              className="btn btn-warning btn-sm me-2"
                              onClick={() => openFeedbackModal(batch)}
                            >
                              <i className="bi bi-chat-left-text me-1"></i> Send Feedback
                            </button>)}

                            <button
                              className="btn btn-outline-info btn-sm me-2"
                              onClick={() => loadFeedbackHistory(batch.id)}
                            >
                              History
                            </button>

                            {1 == batch.finalize && (<button
                              className="btn btn-success btn-sm me-2"
                              title="Journal generated for these invoices"
                            >
                              <i className="bi bi-chat-left-text me-1"></i> Completed
                            </button>)}

                            {0 == batch.finalize && (
                              <button
                                className="btn btn-dark btn-sm me-2"
                                onClick={() => openGenerateModal(batch)}
                              >
                                <i className="bi bi-gear me-1"></i> Generate Entries
                              </button>
                            )}

                            {/* DOWNLOAD */}
                            <button
                              className="btn btn-primary btn-sm me-2"
                              onClick={() => handleDownload(batch.id)}
                            >
                              <i className="bi bi-download me-1"></i> Download
                            </button>


                          </td>
                        </tr>
                      ))}
                    </tbody>

                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ================= FEEDBACK MODAL ================= */}
      {showModal && selectedBatch && (
        <div
          className="modal fade show"
          style={{ display: "block", background: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog">
            <div className="modal-content">

              <div className="modal-header">
                <h5 className="modal-title">Send Invoice Feedback</h5>
                <button className="btn-close" onClick={() => setShowModal(false)}></button>
              </div>

              <div className="modal-body">

                {/* DETAILS BOX */}
                <div className="border rounded p-2 mb-3 bg-light">

                  <div className="row mb-1">
                    <div className="col-6 fw-bold">Month:</div>
                    <div className="col-6 text-end">
                      {new Date(selectedBatch.year, selectedBatch.month - 1).toLocaleDateString(
                        "en-US",
                        { month: "long" }
                      )}
                    </div>
                  </div>

                  <div className="row mb-1">
                    <div className="col-6 fw-bold">Year:</div>
                    <div className="col-6 text-end">{selectedBatch.year}</div>
                  </div>

                  <div className="row mb-1">
                    <div className="col-6 fw-bold">Branch:</div>
                    <div className="col-6 text-end">{selectedBatch.branch?.name}</div>
                  </div>

                  <div className="row mb-1">
                    <div className="col-6 fw-bold">Total Tenants:</div>
                    <div className="col-6 text-end">{selectedBatch.invoices.length}</div>
                  </div>

                  <div className="row mb-1">
                    <div className="col-6 fw-bold">Invoice Date:</div>
                    <div className="col-6 text-end">
                      {new Date(selectedBatch.created_at).toLocaleDateString()}
                    </div>
                  </div>

                </div>

                <label className="form-label">Feedback</label>
                <textarea
                  className="form-control"
                  rows="4"
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  placeholder="Enter your feedback..."
                ></textarea>

              </div>

              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Close
                </button>

                <button
                  className="btn btn-primary"
                  onClick={submitFeedback}
                  disabled={submitting}
                >
                  {submitting ? "Sending..." : "Submit Feedback"}
                </button>
              </div>

            </div>
          </div>
        </div>
      )}

      {/* ================= FEEDBACK HISTORY MODAL ================= */}
      {showHistoryModal && (
        <div
          className="modal fade show"
          style={{ display: "block", background: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">

              <div className="modal-header">
                <h5 className="modal-title">Action & Feedback History</h5>
                <button className="btn-close" onClick={() => setShowHistoryModal(false)} />
              </div>

              <div className="modal-body">
                {historyLoading ? (
                  <p>Loading...</p>
                ) : feedbackHistory.length === 0 ? (
                  <p className="text-muted">No feedback submitted yet.</p>
                ) : (
                  <ul className="list-group">
                    {feedbackHistory.map((fb, index) => (
                      <li key={index} className="list-group-item">
                        <strong>Date:</strong>{" "}
                        {new Date(fb.created_at).toLocaleString("en-GB")}
                        <br />
                        <strong>Feedback:</strong> {fb.feedback}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowHistoryModal(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Load& generate journals */}
      {showGenerateModal && (
        <div
          className="modal fade show"
          style={{ display: "block", background: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-xl">
            <div className="modal-content">

              <div className="modal-header">
                <h5 className="modal-title">Generate Journal Entries</h5>
                <button className="btn-close" onClick={() => setShowGenerateModal(false)} />
              </div>

              <div className="modal-body">
                <p className="fw-bold">
                  Showing all invoices from batch: &nbsp;
                  <span className="text-primary">{selectedBatch?.id}</span>
                </p>

                {generateInvoices.length === 0 ? (
                  <p>No invoices available.</p>
                ) : (
                  <table className="table table-bordered table-sm">
                    <thead className="table-light">
                      <tr>
                        <th width="50" className="text-center" title="Select All">
                          <input
                            type="checkbox"
                            onChange={handleSelectAll}
                            checked={
                              generateInvoices.length > 0 &&
                              selectedInvoices.length === generateInvoices.length
                            }
                          />
                        </th>
                        <th>Tenant</th>
                        <th>Invoice No</th>
                        <th>Invoice Date</th>
                        <th className="text-end">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {generateInvoices.map((inv) => (
                        <tr key={inv.id}>
                          <td className="text-center">
                            <input
                              type="checkbox"
                              checked={selectedInvoices.some((x) => x.id === inv.id)}
                              onChange={() => toggleInvoice(inv)}
                            />
                          </td>
                          <td>{inv.client.name}</td>
                          <td>{inv.invoice_no}</td>
                          <td>
                            {inv.invoice_date
                              ? new Date(inv.invoice_date).toLocaleDateString()
                              : "-"}
                          </td>
                          <td className="text-end">{inv.total_amount}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowGenerateModal(false)}
                >
                  Close
                </button>

                <button
                  className="btn btn-success"
                  disabled={
                    submittingGenerate ||
                    generateInvoices.length === 0 ||
                    selectedInvoices.length !== generateInvoices.length
                  }
                  onClick={submitGenerate}
                >
                  {submittingGenerate ? "Generating..." : "Generate Entries"}
                </button>
              </div>

            </div>
          </div>
        </div>
      )}



    </>
  );
}

export default MonthlyInvoice;